import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Clock, Users, Phone, Utensils, PartyPopper, Wallet, Tv, Loader2, RefreshCw,
  UserPlus, Table, Sparkles, PhoneCall, XCircle, CheckCircle2, AlertTriangle,
  ArrowRight, Star, MapPin,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// ══════════════════════════════════════════════════════════════
// 5 groups + summary
// ══════════════════════════════════════════════════════════════
const GROUPS = [
  { key: "expected_now", label: "Expected Now",  tone: "border-amber-300 bg-amber-50",   icon: Clock },
  { key: "waiting",       label: "Waiting",        tone: "border-yellow-300 bg-yellow-50", icon: Users },
  { key: "table_ready",   label: "Table Ready",    tone: "border-sky-300 bg-sky-50",       icon: Table },
  { key: "arriving_soon", label: "Arriving Soon",  tone: "border-slate-300 bg-slate-50",   icon: ArrowRight },
  { key: "seated",        label: "Seated",         tone: "border-emerald-300 bg-emerald-50", icon: CheckCircle2 },
  { key: "late",          label: "Late — Please Call", tone: "border-rose-300 bg-rose-50", icon: AlertTriangle },
];

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const timingLabel = (item) => {
  if (item.group === "expected_now") return "Expected now";
  if (item.group === "arriving_soon" && item.minutes_until_arrival != null)
    return `Arriving in ${item.minutes_until_arrival} min`;
  if (item.group === "late" && item.minutes_until_arrival != null)
    return `${Math.abs(item.minutes_until_arrival)} min late`;
  if (item.group === "waiting" && item.waiting_minutes != null)
    return `Waiting ${item.waiting_minutes} min`;
  if (item.group === "table_ready" && item.table_number)
    return `Table ${item.table_number} ready`;
  if (item.group === "seated" && item.table_number)
    return `Seated · Table ${item.table_number}`;
  return "";
};

// ══════════════════════════════════════════════════════════════
export default function LiveQueueTab({ session, center, centers = [], canPickCenter = false, onCenterChange }) {
  const [queue, setQueue] = useState({ tokens: [], upcoming: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [tables, setTables] = useState([]);
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [walkInForm, setWalkInForm] = useState({ guest_name: "", mobile: "", adults: 2, children: 0, special_request: "", seating_preference: "" });
  const [tableDialog, setTableDialog] = useState(null); // { booking_id, recommendation, alternatives }

  const load = useCallback(async () => {
    if (!session?.token || !center) return;
    setLoading(true);
    try {
      const [q, t] = await Promise.all([
        fetch(`${API}/api/booking-tokens/live-queue`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: session.token, center }),
        }).then((r) => r.json()),
        fetch(`${API}/api/booking-tokens/tables/list`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: session.token, center }),
        }).then((r) => r.json()),
      ]);
      setQueue({
        tokens: q.tokens || [],
        upcoming: q.upcoming || [],
        summary: q.summary || {},
      });
      setTables(t.tables || []);
    } catch { toast.error("Failed to load queue"); }
    finally { setLoading(false); }
  }, [session?.token, center]);

  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);

  // ── Actions ─────────────────────────────────────────────────
  const guestArrived = async (booking_id, seating_preference) => {
    try {
      const res = await fetch(`${API}/api/booking-tokens/check-in`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, booking_id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "check-in failed");
      toast.success(`Token ${data.token_number} generated`);
      // Immediately fetch recommendation
      const rec = await fetch(`${API}/api/booking-tokens/recommend-table`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, booking_id, seating_preference: seating_preference || "" }),
      }).then((r) => r.json()).catch(() => ({}));
      setTableDialog({ booking_id, token_number: data.token_number, recommendation: rec.recommended, alternatives: rec.alternatives || [] });
      await load();
    } catch (e) { toast.error(e.message || "Arrival failed"); }
  };

  const walkIn = async () => {
    if (!walkInForm.guest_name || !walkInForm.mobile) { toast.error("Name + mobile required"); return; }
    try {
      const res = await fetch(`${API}/api/booking-tokens/walk-in`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, ...walkInForm, total_pax: Number(walkInForm.adults || 0) + Number(walkInForm.children || 0) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "walk-in failed");
      toast.success(`Token ${data.token_number} generated`);
      setWalkInOpen(false);
      setWalkInForm({ guest_name: "", mobile: "", adults: 2, children: 0, special_request: "", seating_preference: "" });
      // Auto-recommend
      const rec = await fetch(`${API}/api/booking-tokens/recommend-table`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, booking_id: data.booking_id, seating_preference: walkInForm.seating_preference || "" }),
      }).then((r) => r.json()).catch(() => ({}));
      setTableDialog({ booking_id: data.booking_id, token_number: data.token_number, recommendation: rec.recommended, alternatives: rec.alternatives || [] });
      await load();
    } catch (e) { toast.error(e.message || "Walk-in failed"); }
  };

  const assignTable = async (booking_id, table_number) => {
    try {
      const res = await fetch(`${API}/api/booking-tokens/assign-table`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, booking_id, table_number }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "assign failed");
      // Auto-call so the token board updates
      await fetch(`${API}/api/booking-tokens/call`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, booking_id }),
      });
      toast.success(`Called guest to Table ${table_number}`);
      setTableDialog(null);
      await load();
    } catch (e) { toast.error(e.message || "Assign failed"); }
  };

  const seatGuest = async (booking_id) => {
    await fetch(`${API}/api/booking-tokens/seat`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: session.token, center, booking_id }),
    });
    await load();
  };

  const markNoShow = async (booking_id) => {
    await fetch(`${API}/api/booking-tokens/no-show`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: session.token, center, booking_id }),
    });
    toast.success("Marked as no-show");
    await load();
  };

  const completeBooking = async (booking_id) => {
    await fetch(`${API}/api/booking-tokens/complete`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: session.token, center, booking_id }),
    });
    toast.success("Booking closed");
    await load();
  };

  const seedTables = async () => {
    await fetch(`${API}/api/booking-tokens/tables/auto-seed`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: session.token, center }),
    });
    toast.success("Default tables seeded");
    await load();
  };

  // ── Center gate ─────────────────────────────────────────────
  if (!center) {
    return (
      <div className="space-y-4" data-testid="live-queue-tab">
        <Card className="border-[#8B0000]/30">
          <CardContent className="p-6 text-center space-y-3">
            <div className="text-lg font-semibold text-[#8B0000] flex items-center justify-center gap-2">
              <Users className="w-5 h-5" /> Live Operations
            </div>
            <p className="text-sm text-slate-600">Please select a centre to open the auto-queue cockpit.</p>
            {canPickCenter && centers.length > 0 && (
              <div className="max-w-xs mx-auto">
                <Select onValueChange={(v) => onCenterChange && onCenterChange(v)}>
                  <SelectTrigger data-testid="live-queue-center-picker"><SelectValue placeholder="Select centre..." /></SelectTrigger>
                  <SelectContent>
                    {centers.map((c) => <SelectItem key={c.code || c} value={c.code || c}>{c.code || c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Combined list (upcoming + tokens) grouped ───────────────
  const combined = [...(queue.upcoming || []), ...(queue.tokens || [])];
  const byGroup = Object.fromEntries(GROUPS.map((g) => [g.key, []]));
  combined.forEach((item) => {
    const g = item.group || "arriving_soon";
    if (!byGroup[g]) byGroup[g] = [];
    byGroup[g].push(item);
  });

  const s = queue.summary || {};

  return (
    <div className="space-y-4" data-testid="live-queue-tab">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {canPickCenter && centers.length > 0 && (
          <div className="w-52">
            <Select value={center} onValueChange={(v) => onCenterChange && onCenterChange(v)}>
              <SelectTrigger className="h-9" data-testid="live-queue-center-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {centers.map((c) => <SelectItem key={c.code || c} value={c.code || c}>{c.code || c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {!canPickCenter && (
          <Badge variant="outline" className="text-sm px-3 py-1 border-[#8B0000]/40 text-[#8B0000]">{center}</Badge>
        )}
        <Button onClick={() => setWalkInOpen(true)} className="bg-[#8B0000] hover:bg-[#7a0000]" data-testid="walkin-open-btn">
          <UserPlus className="w-4 h-4 mr-1" /> New Walk-in
        </Button>
        <Button variant="outline" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />} Refresh
        </Button>
        <Button variant="outline" onClick={seedTables} data-testid="seed-tables-btn">
          <Table className="w-4 h-4 mr-1" /> Seed Default Tables
        </Button>
        <Button variant="outline" onClick={() => window.open(`/queue-display/${center}`, "_blank")} data-testid="open-tv-display-btn">
          <Tv className="w-4 h-4 mr-1" /> Open TV Display
        </Button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="live-queue-summary">
        <Tile label="Today's Bookings" value={s.today_bookings ?? 0} tone="sky" />
        <Tile label="Arriving Soon"   value={s.arriving_soon ?? 0} tone="slate" />
        <Tile label="Expected Now"    value={s.expected_now ?? 0} tone="amber" />
        <Tile label="Waiting"          value={s.currently_waiting ?? 0} tone="yellow" />
        <Tile label="Table Ready"      value={s.table_ready ?? 0} tone="cyan" />
        <Tile label="Seated"           value={s.guests_seated ?? 0} tone="emerald" />
        <Tile label="Tables Free"      value={s.tables_available ?? 0} tone="teal" />
        <Tile label="Avg Wait (min)"   value={s.avg_wait_min ?? 0} tone="rose" />
      </div>

      {/* Grouped booking cards */}
      <div className="space-y-4">
        {GROUPS.map((g) => {
          const items = byGroup[g.key] || [];
          if (items.length === 0) return null;
          const GIcon = g.icon;
          return (
            <div key={g.key} data-testid={`group-${g.key}`}>
              <div className={`flex items-center gap-2 px-3 py-2 rounded-t-md border-2 border-b-0 ${g.tone}`}>
                <GIcon className="w-4 h-4" />
                <h3 className="font-bold text-sm uppercase tracking-wide">{g.label}</h3>
                <Badge variant="outline">{items.length}</Badge>
              </div>
              <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 p-3 rounded-b-md border-2 border-t-0 ${g.tone}`}>
                {items.map((it) => (
                  <BookingCard
                    key={it.booking_id}
                    item={it}
                    tables={tables}
                    onArrived={() => guestArrived(it.booking_id, it.seating_preference)}
                    onCall={() => {
                      // Open picker with recommendation flow
                      fetch(`${API}/api/booking-tokens/recommend-table`, {
                        method: "POST", headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ token: session.token, center, booking_id: it.booking_id, seating_preference: it.seating_preference || "" }),
                      }).then((r) => r.json()).then((rec) => {
                        setTableDialog({ booking_id: it.booking_id, token_number: it.token_number, recommendation: rec.recommended, alternatives: rec.alternatives || [] });
                      }).catch(() => setTableDialog({ booking_id: it.booking_id, token_number: it.token_number }));
                    }}
                    onSeat={() => seatGuest(it.booking_id)}
                    onComplete={() => completeBooking(it.booking_id)}
                    onNoShow={() => markNoShow(it.booking_id)}
                    onChangeTable={() => {
                      setTableDialog({ booking_id: it.booking_id, token_number: it.token_number, recommendation: null, alternatives: [] });
                    }}
                  />
                ))}
              </div>
            </div>
          );
        })}
        {combined.length === 0 && !loading && (
          <Card><CardContent className="p-8 text-center text-slate-500">No bookings or tokens yet today.</CardContent></Card>
        )}
      </div>

      {/* Walk-in Dialog */}
      <Dialog open={walkInOpen} onOpenChange={setWalkInOpen}>
        <DialogContent data-testid="walkin-dialog">
          <DialogHeader><DialogTitle>New Walk-in</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Guest Name *</Label>
                <Input value={walkInForm.guest_name} onChange={(e) => setWalkInForm((f) => ({ ...f, guest_name: e.target.value }))} data-testid="walkin-name" />
              </div>
              <div>
                <Label>Mobile *</Label>
                <Input value={walkInForm.mobile} onChange={(e) => setWalkInForm((f) => ({ ...f, mobile: e.target.value }))} data-testid="walkin-mobile" />
              </div>
              <div>
                <Label>Adults</Label>
                <Input type="number" min={0} value={walkInForm.adults} onChange={(e) => setWalkInForm((f) => ({ ...f, adults: e.target.value }))} data-testid="walkin-adults" />
              </div>
              <div>
                <Label>Children</Label>
                <Input type="number" min={0} value={walkInForm.children} onChange={(e) => setWalkInForm((f) => ({ ...f, children: e.target.value }))} data-testid="walkin-children" />
              </div>
              <div className="col-span-2">
                <Label>Seating Preference</Label>
                <Select value={walkInForm.seating_preference} onValueChange={(v) => setWalkInForm((f) => ({ ...f, seating_preference: v }))}>
                  <SelectTrigger data-testid="walkin-seating"><SelectValue placeholder="No Preference" /></SelectTrigger>
                  <SelectContent>
                    {["No Preference","Window","Family Area","Quiet Area","Ground Floor","Accessibility Required","High Chair Required","Private Area","Other"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Special Request</Label>
                <Input value={walkInForm.special_request} onChange={(e) => setWalkInForm((f) => ({ ...f, special_request: e.target.value }))} data-testid="walkin-special" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWalkInOpen(false)}>Cancel</Button>
            <Button onClick={walkIn} data-testid="walkin-submit">Create Walk-in</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Table Suggestion Dialog */}
      <Dialog open={!!tableDialog} onOpenChange={(o) => !o && setTableDialog(null)}>
        <DialogContent data-testid="table-suggest-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#8B0000]" /> Call Guest — Choose Table
              {tableDialog?.token_number && <Badge>Token {tableDialog.token_number}</Badge>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {tableDialog?.recommendation && (
              <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Star className="w-4 h-4 text-emerald-700" />
                  <b>Recommended</b>
                </div>
                <div className="text-sm">
                  <span className="font-bold text-lg">Table {tableDialog.recommendation.table_number}</span>
                  · {tableDialog.recommendation.capacity} seats · {tableDialog.recommendation.zone || "Main"}
                  {tableDialog.recommendation.reason && <div className="text-xs text-slate-600 mt-1">{tableDialog.recommendation.reason}</div>}
                </div>
                <Button className="mt-2 bg-emerald-600 hover:bg-emerald-700" onClick={() => assignTable(tableDialog.booking_id, tableDialog.recommendation.table_number)} data-testid="accept-recommendation-btn">
                  <PhoneCall className="w-4 h-4 mr-1" /> Accept — Call Guest to Table {tableDialog.recommendation.table_number}
                </Button>
              </div>
            )}
            <div>
              <Label className="text-xs">Choose Another Table</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1 max-h-72 overflow-y-auto">
                {tables.filter((t) => t.is_active !== false).map((t) => {
                  const disabled = t.status === "reserved" || t.status === "occupied";
                  return (
                    <button
                      key={t.table_number}
                      disabled={disabled}
                      onClick={() => assignTable(tableDialog.booking_id, t.table_number)}
                      className={`text-left rounded border p-2 hover:border-[#8B0000] ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                      data-testid={`pick-table-${t.table_number}`}
                    >
                      <div className="font-mono font-bold">Table {t.table_number}</div>
                      <div className="text-[11px] text-slate-600">
                        {t.capacity} seats · {t.zone || "Main"}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider">
                        {t.status || "available"}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTableDialog(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Small tile ────────────────────────────────────────────────
function Tile({ label, value, tone = "sky" }) {
  const toneMap = {
    sky: "bg-sky-50 border-sky-200 text-sky-900",
    slate: "bg-slate-50 border-slate-200 text-slate-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    yellow: "bg-yellow-50 border-yellow-200 text-yellow-900",
    cyan: "bg-cyan-50 border-cyan-200 text-cyan-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    teal: "bg-teal-50 border-teal-200 text-teal-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
  };
  return (
    <div className={`rounded-lg border-2 p-2 ${toneMap[tone] || toneMap.sky}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold">{label}</div>
      <div className="text-xl font-extrabold">{value}</div>
    </div>
  );
}

// ── Booking / Token Card ─────────────────────────────────────
function BookingCard({ item, onArrived, onCall, onSeat, onComplete, onNoShow, onChangeTable }) {
  const isToken = Boolean(item.token_number);
  const status = item.group;
  return (
    <div className="rounded-lg bg-white border border-slate-200 shadow-sm p-3 space-y-1.5" data-testid={`booking-card-${item.booking_id}`}>
      <div className="flex items-center justify-between">
        <div className="font-semibold text-slate-900 text-sm">{item.guest_name || "—"}</div>
        {item.token_number && <Badge className="bg-[#8B0000] text-white">{item.token_number}</Badge>}
      </div>
      <div className="flex items-center gap-2 text-xs text-slate-600 flex-wrap">
        <Clock className="w-3 h-3" /> <span className="font-medium">{item.time_slot || fmtTime(item.actual_arrival) || "—"}</span>
        <span className="text-slate-400">·</span>
        <Users className="w-3 h-3" /> <span>{item.total_pax || (Number(item.adults||0)+Number(item.children||0)) || 1}</span>
        {(item.mobile) && (<><span className="text-slate-400">·</span><Phone className="w-3 h-3" /><span className="font-mono">{item.mobile}</span></>)}
      </div>
      {item.celebration_type && (
        <div className="flex items-center gap-1 text-xs text-purple-700"><PartyPopper className="w-3 h-3" /> {item.celebration_type}</div>
      )}
      {item.seating_preference && (
        <div className="flex items-center gap-1 text-xs text-slate-600"><MapPin className="w-3 h-3" /> {item.seating_preference}</div>
      )}
      {item.special_request && (
        <div className="text-xs italic text-slate-500 truncate" title={item.special_request}>&ldquo;{item.special_request}&rdquo;</div>
      )}
      {item.menu_decided && (
        <div className="flex items-center gap-1 text-xs"><Utensils className="w-3 h-3 text-emerald-600" /> Menu {item.menu_decided}</div>
      )}
      {item.booking_source && (
        <div className="text-[10px] text-slate-500 uppercase tracking-wide">Source: {item.booking_source}</div>
      )}
      <div className="text-[11px] font-semibold text-slate-700">{timingLabel(item)}</div>
      {item.table_number && (
        <div className="text-xs font-bold text-sky-800 flex items-center gap-1"><Table className="w-3.5 h-3.5" /> Table {item.table_number}</div>
      )}

      {/* Action row */}
      <div className="flex flex-wrap gap-1 pt-1">
        {!isToken && status !== "late" && (
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8" onClick={onArrived} data-testid={`arrived-${item.booking_id}`}>
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Guest Arrived
          </Button>
        )}
        {status === "late" && (
          <>
            <Button size="sm" variant="outline" className="h-8" onClick={() => item.mobile && window.open(`tel:${item.mobile}`)} data-testid={`late-call-${item.booking_id}`}>
              <PhoneCall className="w-3.5 h-3.5 mr-1" /> Call
            </Button>
            <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={onArrived} data-testid={`late-keep-${item.booking_id}`}>
              Keep — Guest Arrived
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-rose-700 border-rose-300" onClick={onNoShow} data-testid={`late-noshow-${item.booking_id}`}>
              <XCircle className="w-3.5 h-3.5 mr-1" /> No Show
            </Button>
          </>
        )}
        {status === "waiting" && (
          <Button size="sm" className="bg-[#8B0000] hover:bg-[#7a0000] text-white h-8" onClick={onCall} data-testid={`call-${item.booking_id}`}>
            <PhoneCall className="w-3.5 h-3.5 mr-1" /> Call Guest — Assign Table
          </Button>
        )}
        {status === "table_ready" && (
          <>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8" onClick={onSeat} data-testid={`seat-${item.booking_id}`}>
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Guest Seated
            </Button>
            <Button size="sm" variant="outline" className="h-8" onClick={onChangeTable} data-testid={`change-${item.booking_id}`}>Change Table</Button>
          </>
        )}
        {status === "seated" && (
          <Button size="sm" variant="outline" className="h-8" onClick={onComplete} data-testid={`complete-${item.booking_id}`}>
            Close / Completed
          </Button>
        )}
      </div>
    </div>
  );
}
